import {
  AGENT_STREAM_VERSION,
  AGENT_TERMINAL_STATUSES,
  AgentArtifact,
  AgentCitation,
  AgentEvent,
  AgentMessageState,
  AgentRequestStatus,
  AgentRequestStatusResponse,
  AgentSource,
} from "@/types/agent";

const AGENT_EVENT_TYPES = new Set([
  "response.started",
  "stage.started",
  "web_search.searching",
  "web_search.completed",
  "web_search.failed",
  "text.delta",
  "text.snapshot",
  "citation.added",
  "source.added",
  "artifact.generating",
  "artifact.ready",
  "artifact.failed",
  "response.deferred",
  "response.completed",
  "response.failed",
]);

export const isAgentEvent = (value: any): value is AgentEvent =>
  value?.version === "1" &&
  typeof value?.requestId === "string" &&
  Number.isSafeInteger(value?.sequence) &&
  value.sequence > 0 &&
  AGENT_EVENT_TYPES.has(value?.type) &&
  value?.payload !== null &&
  typeof value?.payload === "object";

export const createAgentMessageState = (
  requestId: string,
): AgentMessageState => ({
  protocolVersion: AGENT_STREAM_VERSION,
  requestId,
  status: "STARTING",
  lastSequence: 0,
  textSnapshot: "",
  citations: [],
  sources: [],
  artifacts: [],
  terminal: false,
  deferred: false,
  searching: false,
  searchStarted: false,
  searchCompleted: false,
});

const upsertArtifact = (
  artifacts: AgentArtifact[],
  incoming: Partial<AgentArtifact>,
  fallbackStatus: AgentArtifact["status"],
) => {
  if (!incoming.artifactId) return artifacts;
  const index = artifacts.findIndex(
    (artifact) => artifact.artifactId === incoming.artifactId,
  );
  const next = {
    ...(index >= 0 ? artifacts[index] : {}),
    ...incoming,
    artifactId: incoming.artifactId,
    status: incoming.status || fallbackStatus,
  } as AgentArtifact;
  if (index < 0) return [...artifacts, next];
  const result = [...artifacts];
  result[index] = next;
  return result;
};

const appendUnique = <T,>(
  values: T[],
  incoming: T,
  key: (value: T) => string,
) => {
  const incomingKey = key(incoming);
  if (!incomingKey || values.some((value) => key(value) === incomingKey)) {
    return values;
  }
  return [...values, incoming];
};

const citationFromPayload = (event: AgentEvent): AgentCitation | null => {
  const value = event.payload.citation || event.payload;
  if (!value?.url) return null;
  const textRevision = value.textRevision ?? event.textRevision ?? null;
  const textSha256 = value.textSha256 ?? event.payload.textSha256 ?? null;
  return { ...value, textRevision, textSha256 };
};

const sourceFromPayload = (event: AgentEvent): AgentSource | null => {
  const value = event.payload.source || event.payload;
  return value?.url ? value : null;
};

const normalizeCitation = (
  citation: AgentCitation,
  state: Pick<AgentMessageState, "textRevision" | "textSha256">,
): AgentCitation => {
  const revisionMatches =
    citation.textRevision !== null &&
    citation.textRevision !== undefined &&
    state.textRevision !== null &&
    state.textRevision !== undefined &&
    citation.textRevision === state.textRevision;
  const hashMatches =
    Boolean(citation.textSha256) &&
    Boolean(state.textSha256) &&
    citation.textSha256 === state.textSha256;
  return {
    ...citation,
    highlightable:
      citation.indexValid === true && revisionMatches && hashMatches,
  };
};

export const reduceAgentEvent = (
  current: AgentMessageState | undefined,
  event: AgentEvent,
): AgentMessageState => {
  const state = current || createAgentMessageState(event.requestId);
  if (state.requestId !== event.requestId) return state;
  if (event.sequence <= state.lastSequence) return state;
  if (state.terminal) return state;

  let next: AgentMessageState = {
    ...state,
    lastSequence: event.sequence,
    updatedAt: event.occurredAt || state.updatedAt,
  };
  const payload = event.payload || {};

  switch (event.type) {
    case "response.started":
      next.status = "RUNNING";
      next.currentStage = event.stage || next.currentStage;
      break;
    case "stage.started":
      if (typeof payload.paintingGuide === "boolean") next.paintingGuide = payload.paintingGuide;
      next.status = "RUNNING";
      next.currentStage = event.stage || payload.stage || next.currentStage;
      break;
    case "web_search.searching":
      next.searching = true;
      next.searchStarted = true;
      next.searchCompleted = false;
      break;
    case "web_search.completed":
      next.searching = false;
      next.searchStarted = true;
      next.searchCompleted = true;
      break;
    case "web_search.failed":
      next.searching = false;
      next.searchStarted = true;
      next.searchCompleted = true;
      next.errorCode = payload.code || payload.errorCode;
      break;
    case "text.delta": {
      const revision = event.textRevision ?? payload.textRevision ?? next.textRevision;
      if (
        revision !== undefined &&
        revision !== null &&
        next.textRevision !== undefined &&
        next.textRevision !== null &&
        revision !== next.textRevision
      ) {
        next.textSnapshot = "";
        next.citations = [];
      }
      next.textRevision = revision;
      next.textSha256 = payload.textSha256 || next.textSha256;
      next.textSnapshot += String(payload.delta ?? payload.text ?? "");
      break;
    }
    case "text.snapshot": {
      const incomingRevision =
        event.textRevision ?? payload.textRevision ?? next.textRevision;
      if (
        incomingRevision !== undefined &&
        incomingRevision !== null &&
        next.textRevision !== undefined &&
        next.textRevision !== null &&
        incomingRevision > next.textRevision
      ) {
        // A higher-revision snapshot is an atomic text replacement (citation
        // repair), so offsets from the previous revision must not remain visible.
        // Sources are revision-independent and intentionally remain available.
        next.citations = [];
      }
      next.textSnapshot = String(payload.textSnapshot ?? payload.text ?? "");
      next.textRevision = incomingRevision;
      next.textSha256 = payload.textSha256 || next.textSha256;
      next.citations = next.citations.map((citation) =>
        normalizeCitation(citation, next),
      );
      break;
    }
    case "citation.added": {
      const citation = citationFromPayload(event);
      if (citation) {
        next.citations = appendUnique(
          next.citations,
          normalizeCitation(citation, next),
          (value) =>
            [
              value.webSearchCallId,
              value.url,
              value.startIndex,
              value.endIndex,
              value.textRevision,
            ].join("|"),
        );
      }
      break;
    }
    case "source.added": {
      const source = sourceFromPayload(event);
      if (source) {
        next.sources = appendUnique(
          next.sources,
          source,
          (value) => [value.webSearchCallId, value.url].join("|"),
        );
      }
      break;
    }
    case "artifact.generating":
      next.status = "WAITING_ARTIFACT";
      next.currentStage = "ARTIFACT";
      next.artifacts = upsertArtifact(
        next.artifacts,
        payload.artifact || payload,
        "GENERATING",
      );
      break;
    case "artifact.ready":
      next.currentStage = "FINAL";
      next.artifacts = upsertArtifact(
        next.artifacts,
        payload.artifact || payload,
        "READY",
      );
      break;
    case "artifact.failed":
      next.artifacts = upsertArtifact(
        next.artifacts,
        payload.artifact || payload,
        "FAILED",
      );
      break;
    case "response.deferred":
      next.status = (payload.requestStatus || "WAITING_ARTIFACT") as AgentRequestStatus;
      next.deliveryMode = "BACKGROUND";
      next.deferred = true;
      next.requestStatusPath = payload.requestStatusPath;
      next.retryAfterMs = Number(payload.retryAfterMs) || undefined;
      if (payload.artifactId) {
        next.artifacts = upsertArtifact(
          next.artifacts,
          {
            artifactId: payload.artifactId,
            status: payload.artifactStatus,
            statusPath: payload.artifactStatusPath,
          },
          "GENERATING",
        );
      }
      break;
    case "response.completed":
      if (typeof payload.paintingGuide === "boolean") next.paintingGuide = payload.paintingGuide;
      next.status = "COMPLETED";
      next.terminal = true;
      next.searching = false;
      break;
    case "response.failed":
      next.status = (payload.status ||
        (payload.code === "AGENT_CANCELLED"
          ? "CANCELLED"
          : payload.code === "AGENT_OUTCOME_UNKNOWN"
          ? "OUTCOME_UNKNOWN"
          : payload.partial
          ? "PARTIAL"
          : "FAILED")) as AgentRequestStatus;
      next.terminal = true;
      next.searching = false;
      next.partial = Boolean(payload.partial);
      next.errorCode = payload.code || payload.errorCode;
      next.errorMessage = payload.message || payload.errorMessage;
      break;
  }

  return next;
};

export const reduceAgentStatus = (
  current: AgentMessageState | undefined,
  status: AgentRequestStatusResponse,
): AgentMessageState => {
  const state = current || createAgentMessageState(status.requestId);
  if (state.requestId !== status.requestId) return state;
  const deliveryMode = status.deliveryMode || state.deliveryMode;
  const terminal = AGENT_TERMINAL_STATUSES.includes(status.status);
  // 取消时状态查询与 SSE 并行；较晚返回的运行中快照不能覆盖已确认的终态。
  if (state.terminal && !terminal) return state;
  const statusSnapshotSequence = status.snapshotSequence || 0;
  if (!terminal && statusSnapshotSequence < state.lastSequence) return state;
  const statusSnapshotIsCurrent = statusSnapshotSequence >= state.lastSequence;
  const next: AgentMessageState = {
    ...state,
    status: status.status,
    deliveryMode,
    streamRecoverable: status.streamRecoverable,
    textSnapshot: status.textSnapshot ?? state.textSnapshot,
    paintingGuide: status.paintingGuide ?? state.paintingGuide,
    textRevision: status.textRevision ?? state.textRevision,
    textSha256: status.textSha256 ?? state.textSha256,
    lastSequence: Math.max(state.lastSequence, status.snapshotSequence || 0),
    citations: (statusSnapshotIsCurrent && status.citations !== undefined
      ? status.citations
      : state.citations).map((citation) =>
      normalizeCitation(citation, status),
    ),
    sources:
      statusSnapshotIsCurrent && status.sources !== undefined
        ? status.sources
        : state.sources,
    artifacts:
      statusSnapshotIsCurrent && status.artifacts !== undefined
        ? status.artifacts
        : state.artifacts,
    requestStatusPath: status.requestStatusPath || state.requestStatusPath,
    retryAfterMs: status.retryAfterMs,
    errorCode: status.errorCode || state.errorCode,
    errorMessage: status.errorMessage || status.message || state.errorMessage,
    searchWarningCode: status.searchWarningCode || state.searchWarningCode,
    partial: status.partial ?? state.partial,
    updatedAt: status.updatedAt || state.updatedAt,
    terminal,
    deferred:
      !terminal &&
      (state.deferred ||
        deliveryMode === "BACKGROUND" ||
        status.status === "WAITING_ARTIFACT"),
    searching: status.status === "RUNNING" ? state.searching : false,
    searchStarted:
      state.searchStarted ||
      Boolean(status.searchExecuted) ||
      Boolean(status.citations?.length || status.sources?.length),
    searchCompleted:
      state.searchCompleted ||
      Boolean(status.searchSucceeded) ||
      Boolean(status.citations?.length || status.sources?.length),
  };
  return next;
};

export const getAgentEventId = (state?: AgentMessageState) =>
  state?.lastSequence ? `${state.requestId}:${state.lastSequence}` : undefined;
